// OSIS - Cheltuieli Operaționale
import React, { useState } from 'react';
import { 
  CreditCard, 
  TrendingUp, 
  TrendingDown,
  Calendar,
  DollarSign,
  Package,
  Users,
  Zap,
  FileText,
  Filter,
  Download,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  AlertCircle
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Modal from '../../components/Modal';

const CheltuieliOperationale = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('luna');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('toate');

  // Mock data pentru cheltuieli OSIS
  const expenseCategories = [
    { id: 'materiale', name: 'Materiale', color: 'blue', icon: Package },
    { id: 'salarii', name: 'Salarii & Personal', color: 'green', icon: Users },
    { id: 'utilitati', name: 'Utilități', color: 'yellow', icon: Zap },
    { id: 'transport', name: 'Transport & Logistică', color: 'purple', icon: CreditCard },
    { id: 'echipamente', name: 'Echipamente & Mentenanță', color: 'red', icon: FileText },
    { id: 'marketing', name: 'Marketing & Publicitate', color: 'pink', icon: TrendingUp },
    { id: 'altele', name: 'Altele', color: 'gray', icon: FileText }
  ];

  const expenses = [
    {
      id: 1,
      description: 'rășini epoxidice Sika - Lot 2024-001',
      category: 'materiale',
      amount: 15420.00,
      supplier: 'Sika Romania SRL',
      date: '2024-12-28',
      invoice: 'RH-2024-5678',
      status: 'platit',
      dueDate: '2024-12-30',
      paymentMethod: 'transfer'
    },
    {
      id: 2,
      description: 'Salarii echipa producție - Decembrie',
      category: 'salarii',
      amount: 18500.00,
      supplier: 'Personal OSIS',
      date: '2024-12-25',
      invoice: 'SAL-2024-12',
      status: 'platit',
      dueDate: '2024-12-25',
      paymentMethod: 'virament'
    },
    {
      id: 3,
      description: 'Energia electrică - Decembrie',
      category: 'utilitati',
      amount: 2340.50,
      supplier: 'E.ON Energie Romania',
      date: '2024-12-20',
      invoice: 'EON-2024-1245',
      status: 'platit',
      dueDate: '2024-12-22',
      paymentMethod: 'domiciliere'
    },
    {
      id: 4,
      description: 'Transport proiecte - Clienți Suceava',
      category: 'transport',
      amount: 850.00,
      supplier: 'Transport Rapid SRL',
      date: '2024-12-18',
      invoice: 'TR-2024-0234',
      status: 'pending',
      dueDate: '2025-01-02',
      paymentMethod: 'numerar'
    },
    {
      id: 5,
      description: 'Mentenanță mașini CNC - Trimestru IV',
      category: 'echipamente',
      amount: 4200.00,
      supplier: 'TechServ Industrial',
      date: '2024-12-15',
      invoice: 'TS-2024-0156',
      status: 'platit',
      dueDate: '2024-12-20',
      paymentMethod: 'transfer'
    },
    {
      id: 6,
      description: 'Gaze naturale - Decembrie',
      category: 'utilitati',
      amount: 1680.30,
      supplier: 'E.ON Gaz Romania',
      date: '2024-12-12',
      invoice: 'EOG-2024-0987',
      status: 'platit',
      dueDate: '2024-12-15',
      paymentMethod: 'domiciliere'
    },
    {
      id: 7,
      description: 'Publicitate Google Ads - Decembrie',
      category: 'marketing',
      amount: 1250.00,
      supplier: 'Google Ireland Limited',
      date: '2024-12-10',
      invoice: 'GGL-2024-5432',
      status: 'platit',
      dueDate: '2024-12-10',
      paymentMethod: 'card'
    },
    {
      id: 8,
      description: 'Materiale pardoseală - Comandă urgentă',
      category: 'materiale',
      amount: 8750.00,
      supplier: 'Mapei Romania',
      date: '2024-12-08',
      invoice: 'GG-2024-1876',
      status: 'platit',
      dueDate: '2024-12-10',
      paymentMethod: 'transfer'
    }
  ];

  // Calculuri statistici
  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const paidExpenses = expenses.filter(exp => exp.status === 'platit').reduce((sum, exp) => sum + exp.amount, 0);
  const pendingExpenses = expenses.filter(exp => exp.status === 'pending').reduce((sum, exp) => sum + exp.amount, 0);
  
  const expensesByCategory = expenseCategories.map(cat => ({
    ...cat,
    total: expenses.filter(exp => exp.category === cat.id).reduce((sum, exp) => sum + exp.amount, 0),
    count: expenses.filter(exp => exp.category === cat.id).length
  })).sort((a, b) => b.total - a.total);

  // Filtrare cheltuieli
  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expense.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expense.invoice.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'toate' || expense.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddExpense = () => {
    setSelectedExpense(null);
    setShowAddModal(true);
  };

  const handleEditExpense = (expense) => {
    setSelectedExpense(expense);
    setShowAddModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'platit': 'bg-green-50 text-green-700 border-green-200',
      'pending': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'overdue': 'bg-red-50 text-red-700 border-red-200'
    };
    const labels = {
      'platit': 'Plătit',
      'pending': 'În așteptare',
      'overdue': 'Întârziat'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
        {labels[status] || 'Necunoscut'}
      </span>
    );
  };

  const getCategoryIcon = (categoryId) => {
    const category = expenseCategories.find(cat => cat.id === categoryId);
    if (!category) return FileText;
    return category.icon;
  };

  const getCategoryColor = (categoryId) => {
    const category = expenseCategories.find(cat => cat.id === categoryId);
    if (!category) return 'gray';
    return category.color;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Cheltuieli Operaționale
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Gestionarea tuturor costurilor operaționale OSIS
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="form-select text-sm"
              >
                <option value="saptamana">Această săptămână</option>
                <option value="luna">Această lună</option>
                <option value="trimestru">Acest trimestru</option>
                <option value="an">Acest an</option>
              </select>
              <Button 
                onClick={handleAddExpense}
                className="bg-gradient-to-r from-red-500 to-purple-600 hover:from-red-600 hover:to-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Cheltuială
              </Button>
            </div>
          </div>
        </div>

        {/* Statistici Generale */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl">
                  <CreditCard className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cheltuieli</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(totalExpenses)}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Plătit</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(paidExpenses)}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
                  <TrendingDown className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">În așteptare</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(pendingExpenses)}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Facturi</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {expenses.length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Cheltuieli pe Categorii */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <Card title="Cheltuieli pe Categorii" className="lg:col-span-1">
            <div className="space-y-4">
              {expensesByCategory.map((category) => {
                const Icon = category.icon;
                const percentage = totalExpenses > 0 ? (category.total / totalExpenses) * 100 : 0;
                
                return (
                  <div key={category.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center">
                      <div className={`flex items-center justify-center w-10 h-10 bg-${category.color}-100 dark:bg-${category.color}-900/30 rounded-lg mr-3`}>
                        <Icon className={`w-5 h-5 text-${category.color}-600`} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{category.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{category.count} facturi</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(category.total)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{percentage.toFixed(1)}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card title="Evoluția Cheltuielilor" className="lg:col-span-1">
            <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <p className="text-gray-500 dark:text-gray-400">Grafic cheltuieli în timp</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Va fi implementat în versiunea următoare</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filtre și Căutare */}
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Caută după descriere, furnizor sau factură..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="form-select w-full"
              >
                <option value="toate">Toate categoriile</option>
                {expenseCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <Button variant="outline">
              <Filter className="w-4 h-4 mr-2" />
              Filtre
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </Card>

        {/* Tabel Cheltuieli */}
        <Card title="Lista Cheltuielilor">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Descriere
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Categorie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Furnizor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Sumă
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredExpenses.map((expense) => {
                  const CategoryIcon = getCategoryIcon(expense.category);
                  const categoryColor = getCategoryColor(expense.category);
                  
                  return (
                    <tr key={expense.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {expense.description}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {expense.invoice}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`flex items-center justify-center w-8 h-8 bg-${categoryColor}-100 dark:bg-${categoryColor}-900/30 rounded-lg mr-2`}>
                            <CategoryIcon className={`w-4 h-4 text-${categoryColor}-600`} />
                          </div>
                          <span className="text-sm text-gray-900 dark:text-white">
                            {expenseCategories.find(cat => cat.id === expense.category)?.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {expense.supplier}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                        {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(expense.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {new Date(expense.date).toLocaleDateString('ro-RO')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(expense.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => console.log('View expense', expense.id)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditExpense(expense)}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => console.log('Delete expense', expense.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {filteredExpenses.length === 0 && (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Nu s-au găsit cheltuieli care să corespundă filtrelor.</p>
            </div>
          )}
        </Card>
      </div>

      {/* Modal Adăugare/Editare Cheltuială */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={selectedExpense ? 'Editează Cheltuială' : 'Adaugă Cheltuială Nouă'}
        size="lg"
      >
        <div className="space-y-6">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-white" />
            </div>
            <p className="text-gray-500 dark:text-gray-400">Formularul de adăugare cheltuieli</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Va fi implementat în versiunea următoare</p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CheltuieliOperationale;